import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { useState } from 'react';
import type { ControlPlaneOperationalProjection } from '@internal/spassu-code-contract';
import type { ControlPlaneOnboardingView } from '../hooks/useControlPlaneOnboarding';

type Confirmation = 'revoke' | null;

function ConfirmationDialog(props: {
  action: Confirmation;
  busy: boolean;
  onClose(): void;
  onConfirm(): void;
}) {
  return (
    <Dialog open={props.action !== null} onClose={props.onClose}>
      <DialogTitle>Revogar credencial?</DialogTitle>
      <DialogContent>
        <Typography>
          A credencial atual deixará de acessar esta atividade. Esta ação fica
          registrada na auditoria permanente.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.busy}>
          Cancelar
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={props.onConfirm}
          disabled={props.busy}
        >
          Confirmar revogação
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The canonical projection decides whether there is something to revoke
 * (ADR-009): terminalization revokes the credential while the owner-scoped
 * session row may still read as live, which used to offer "Revogar" beside a
 * "Revogada" chip. The session status only narrows it further.
 */
export function canRevoke(
  view: ControlPlaneOnboardingView,
  projection: ControlPlaneOperationalProjection | null,
): boolean {
  return Boolean(
    projection &&
      ['awaiting_pairing', 'connecting', 'connected'].includes(
        projection.connection.state,
      ) &&
      view.session &&
      !['expired', 'revoked', 'completed'].includes(view.session.status),
  );
}

export function SessionActions(props: {
  view: ControlPlaneOnboardingView;
  projection: ControlPlaneOperationalProjection | null;
}) {
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  if (!canRevoke(props.view, props.projection)) return null;
  const busy = props.view.operation !== 'idle';
  const confirm = () => {
    if (props.view.session && confirmation === 'revoke') {
      void props.view.revoke(props.view.session.id);
    }
    setConfirmation(null);
  };
  return (
    <>
      <Button
        color="secondary"
        onClick={() => setConfirmation('revoke')}
        disabled={busy}
      >
        Revogar credencial
      </Button>
      <ConfirmationDialog
        action={confirmation}
        busy={busy}
        onClose={() => setConfirmation(null)}
        onConfirm={confirm}
      />
    </>
  );
}
